import React, { useRef, useContext, useState, useEffect } from 'react';
import styled from 'styled-components';
import { FeatureAppLoader } from '@feature-hub/react';
import { useMediaQuery } from '@oneaudi/feature-app-utils';
import { VideoAsset, PlayMode } from '../../types';
import { AssetHelper } from '../../helper/assetHelper';
import { isPortrait } from '../../utils/get-orientation';
import { useIntersection } from '../../utils/use-intersection';
import { useViewbox } from '../../utils/use-viewbox';
import { Context } from '../../context';

const VideoPlayerWrapper = styled.div`
  height: 100%;
  position: absolute;
  width: 100%;
  min-height: 100%;
  object-fit: cover;
`;

export interface VideoProps {
  position: number;
  testId: string;
  asset: VideoAsset;
}

export const Video = ({ position, testId, asset, ...ueProps }: VideoProps) => {
  // TODO: we should handle the correct video asset url in the asset helper
  // - we get the source string from editor (type: video/image), (asset source: local uri or full url)
  // - we have a asset source resolver that determines breakpoint (for size) and orientation
  // - Component consumes result of an resolver.

  const {
    sourceDefault,
    sourceMobile,
    playMode,
    thresholdValue,
    imageFallbackMobile,
    imageFallback,
    landscapeCaptions,
    portraitCaptions,
    landscapeAudioDescription,
    portraitAudioDescription,
    portraitAltText,
    landscapeAltText,
    loop,
  } = asset;

  const videoPlayerFeatureAppId = `fa-video-player`;
  const videoPlayerFeatureAppBaseUrl = `https://fa-video-player.cdn.prod.collab.apps.one.audi/v5.4.3`;
  const videoPlayerFeatureAppSrc = `${videoPlayerFeatureAppBaseUrl}/fh/app.js`;
  const videoPlayerFeatureAppServerSrc = `${videoPlayerFeatureAppBaseUrl}/fh/app.node.js`;
  const isMobile = useMediaQuery('(max-width: 768px)');

  const { logger } = useContext(Context);
  const [playing, setPlaying] = useState(false);
  const videoPlayer = useRef<HTMLDivElement>(null);

  const [source, setSource] = useState<string>();
  const { orientation } = useViewbox();

  const entry = useIntersection({
    node: videoPlayer,
    rootMargin: '0% 0% -10% 0%',
  });
  const videoElement = videoPlayer.current?.querySelector('div')?.querySelector('video');

  useEffect(() => {
    if (videoElement) {
      videoElement.load();
      setPlaying(false);
    }
  }, [source]);

  useEffect(() => {
    if (videoElement && source && !playing) {
      if (playMode === PlayMode.autoPlay || position * 100 >= thresholdValue) {
        videoElement.play();
        setPlaying(true);
      }
    }
  }, [position, source, playing]);

  useEffect(() => {
    if (!source && entry?.isIntersecting) {
      setSource(
        AssetHelper.getVideoUrl(
          isPortrait() ? sourceMobile?.assetValue : sourceDefault?.assetValue,
          logger,
        ),
      );
    }
  }, [entry, source, orientation]);

  useEffect(() => {
    if (entry?.isIntersecting === false && videoElement) {
      videoElement.pause();
      setPlaying(false);
    }
  }, [entry]);

  const videoConfig = {
    playsInline: true,
    cover: true,
    videos: {
      xs: {
        loop,
        alt: isMobile ? portraitAltText : landscapeAltText,
        aspectRatio: '16/9',
        src: isMobile ? sourceMobile.assetValue : sourceDefault.assetValue,
        autoPlay: playMode === PlayMode.autoPlay,
        muted: true,
        nativeControls: true,
        poster: isMobile ? imageFallbackMobile.assetValue : imageFallback.assetValue,
        caption: isMobile ? landscapeCaptions?.assetValue : portraitCaptions?.assetValue,
        audioDescription: isMobile
          ? landscapeAudioDescription?.assetValue
          : portraitAudioDescription?.assetValue,
      },
    },
  };

  return (
    <VideoPlayerWrapper
      key={isMobile ? 'mobile' : 'desktop'}
      ref={videoPlayer}
      data-testid={testId}
      role="group"
      aria-label={isMobile ? portraitAltText : landscapeAltText}
      {...ueProps}
    >
      <FeatureAppLoader
        key={isMobile ? 'mobile' : 'desktop'}
        config={videoConfig}
        featureAppId={videoPlayerFeatureAppId}
        serverSrc={videoPlayerFeatureAppServerSrc}
        src={videoPlayerFeatureAppSrc}
      />
    </VideoPlayerWrapper>
  );
};
